import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Trophy, Music, Clock, Camera } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fileUrl } from "../components/site/HeroSlides";

const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();
const mins = (iso, now) => Math.round((new Date(iso) - now) / 60000);
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function useNow() {
  const [n, setN] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setN(new Date()), 15000); return () => clearInterval(t); }, []);
  return n;
}

function Schedule({ events, now }) {
  const today = events.filter((e) => isToday(e.event_datetime));
  const list = (today.length ? today : events.filter((e) => new Date(e.event_datetime) > now)).slice(0, 6);
  return (
    <div data-testid="screen-schedule">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#C8A96B] mb-4"><Clock className="w-4 h-4" /> {today.length ? "Today" : "Coming up"}</p>
      {list.map((e) => {
        const m = mins(e.event_datetime, now);
        const past = m < -30;
        return (
          <div key={e.event_id} className={`flex items-baseline gap-4 py-2.5 border-b border-white/10 ${past ? "opacity-40" : ""}`}>
            <span className="text-white/60 w-20 shrink-0 text-sm">{clock(e.event_datetime)}</span>
            <span className="font-serif text-2xl flex-1">{e.name}</span>
            {m >= 0 && m <= 60 && <span className="text-[#C8A96B] text-sm">in {m} min</span>}
          </div>
        );
      })}
      {!list.length && <p className="text-white/50">Add events in your dashboard to show the schedule here.</p>}
    </div>
  );
}

function Slideshow({ photos }) {
  const [i, setI] = useState(0);
  const [seen, setSeen] = useState(null);
  useEffect(() => { const t = setInterval(() => setI((x) => x + 1), 6000); return () => clearInterval(t); }, []);
  useEffect(() => { if (photos[0] && seen && photos[0].item_id !== seen) setI(0); if (photos[0]) setSeen(photos[0].item_id); }, [photos]); // eslint-disable-line
  if (!photos.length) return <div className="h-full flex flex-col items-center justify-center text-white/40" data-testid="screen-no-photos"><Camera className="w-10 h-10 mb-3" />Guest photos appear here the moment they're posted</div>;
  const p = photos[i % photos.length];
  const fresh = Date.now() - new Date(p.at).getTime() < 120000;
  return (
    <div className="relative h-full rounded-3xl overflow-hidden bg-black" data-testid="screen-slideshow">
      <AnimatePresence mode="wait">
        <motion.img key={p.item_id} src={fileUrl(p.path)} alt="" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} className="absolute inset-0 w-full h-full object-contain" />
      </AnimatePresence>
      <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
        {fresh && <span className="text-[10px] uppercase tracking-[0.25em] bg-[#C8A96B] text-black px-2 py-1 rounded-full mr-3">Just posted</span>}
        <span className="text-lg">{p.by ? `by ${p.by}` : ""}</span>{p.caption && <span className="text-white/70"> · {p.caption}</span>}
      </div>
    </div>
  );
}

function Leaderboard({ rows }) {
  return (
    <div data-testid="screen-leaderboard">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#C8A96B] mb-4"><Trophy className="w-4 h-4" /> Party leaderboard</p>
      {rows.map((r, i) => (
        <motion.div layout key={r.name} className="flex items-center gap-3 py-1.5">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${i === 0 ? "bg-[#C8A96B] text-black" : "bg-white/10"}`}>{i + 1}</span>
          <span className="flex-1 truncate text-lg">{r.name}</span><span className="text-[#C8A96B]">{r.points}</span>
        </motion.div>
      ))}
      {!rows.length && <p className="text-white/50 text-sm">Scores show up once guests start playing.</p>}
    </div>
  );
}

function LiveVote({ r }) {
  const total = r.votes.reduce((a, b) => a + b, 0) || 1;
  return (
    <div data-testid="screen-live-vote">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#C8A96B] mb-2"><Music className="w-4 h-4" /> Guess the first dance · code {r.code}</p>
      <p className="font-serif text-2xl mb-3">{r.prompt}</p>
      {r.options.map((o, i) => (
        <div key={i} className="mb-2">
          <div className="flex justify-between text-sm mb-1"><span className="truncate">{o}</span><span className="text-white/60">{r.votes[i]}</span></div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden"><motion.div className="h-full bg-[#C8A96B]" animate={{ width: `${(r.votes[i] / total) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Reveal({ r }) {
  const right = r.votes[r.correct_index] || 0;
  const total = r.votes.reduce((a, b) => a + b, 0);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[#0b0b0b] flex flex-col items-center justify-center text-center px-10" data-testid="screen-reveal">
      <p className="text-[12px] uppercase tracking-[0.4em] text-[#C8A96B] mb-6">Our first dance is…</p>
      <motion.h1 initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.2, type: "spring" }} className="font-serif font-light text-6xl lg:text-8xl mb-8" data-testid="screen-reveal-song">{r.options[r.correct_index]}</motion.h1>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4 }}>
        <p className="text-xl text-white/70 mb-4">{right} of {total} guests guessed right · +10 points each</p>
        {r.fastest && <p className="text-2xl mb-4" data-testid="screen-reveal-fastest">Fastest correct guess: <span className="text-[#C8A96B]">{r.fastest}</span> (+5 bonus)</p>}
        <p className="text-white/50 max-w-3xl">{r.winners.slice(0, 20).join(" · ")}</p>
      </motion.div>
    </motion.div>
  );
}

export default function ReceptionLivePage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [skew, setSkew] = useState(0);
  const now = useNow();
  useEffect(() => {
    const load = () => api.get(`/public/reception-live/${slug}?k=${encodeURIComponent(sp.get("k") || "")}`).then((r) => { setD(r.data); setSkew(new Date(r.data.server_time).getTime() - Date.now()); }).catch((e) => setErr(formatApiError(e)));
    load(); const t = setInterval(load, 5000); return () => clearInterval(t);
  }, [slug, sp]);

  const autoAlert = useMemo(() => {
    const e = (d?.events || []).find((x) => { const m = mins(x.event_datetime, now); return m >= 0 && m <= 5; });
    return e && { text: `${e.name} in ${mins(e.event_datetime, now) || "less than 1"} minute${mins(e.event_datetime, now) === 1 ? "" : "s"}` };
  }, [d, now]);
  if (err) return <div className="min-h-screen bg-black text-white/60 flex items-center justify-center p-10 text-center" data-testid="screen-error">{err}</div>;
  if (!d) return <div className="min-h-screen bg-black" />;
  const reveal = d.rounds.find((r) => r.revealed && Date.now() + skew - new Date(r.revealed_at).getTime() < 90000);
  const voting = d.rounds.find((r) => r.held && !r.revealed);
  const alert = d.alert || autoAlert;
  const audioSrc = d.alert?.audio_path ? fileUrl(d.alert.audio_path) : null;
  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white p-8 flex flex-col" data-testid="reception-live">
      <header className="flex items-end justify-between mb-6">
        <div><p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A96B]">Live from the reception</p><h1 className="font-serif font-light text-5xl">{d.couple_names}</h1></div>
        <p className="text-white/60 text-2xl">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}{d.hashtag && <span className="ml-4 text-[#C8A96B]">{d.hashtag}</span>}</p>
      </header>
      <AnimatePresence>
        {alert && (
          <motion.div key={alert.text} initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} className="mb-6 rounded-2xl bg-[#C8A96B] text-black px-6 py-4 flex items-center gap-3 text-2xl font-serif" data-testid="screen-alert">
            <Bell className="w-6 h-6 animate-pulse" /> {alert.text}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 min-h-[60vh]">
        <Slideshow photos={d.photos} />
        <aside className="space-y-8 overflow-hidden">
          <Schedule events={d.events} now={now} />
          {voting ? <LiveVote r={voting} /> : <Leaderboard rows={d.leaderboard} />}
        </aside>
      </div>
      <AnimatePresence>{reveal && <Reveal r={reveal} />}</AnimatePresence>
      {audioSrc && <audio key={d.alert.alert_id} src={audioSrc} autoPlay data-testid="screen-alert-voice" />}
    </div>
  );
}
